import { useEffect } from 'react';
import { backendURL } from 'config';

/**
 * Inject a `<link rel="manifest">` + theme-color meta + page title for
 * the resolved brand, and swap the favicon for the brand's logo while
 * the customer is on this brand's storefront. The backend manifest
 * route returns a per-brand manifest so the storefront becomes
 * installable as a PWA.
 *
 * Restores the platform favicon + page title on unmount so navigating
 * away doesn't leak brand metadata.
 */
export default function useBrandManifestAndTitle(brand) {
  useEffect(() => {
    if (!brand?._id) return undefined;

    // Register the storefront service worker (pre-caches /offline.html,
    // stale-while-revalidate for assets). Production only — in dev,
    // CRA's HMR conflicts with SW caching.
    if (
      typeof navigator !== 'undefined' &&
      'serviceWorker' in navigator &&
      process.env.NODE_ENV === 'production'
    ) {
      navigator.serviceWorker
        .register('/storefront-sw.js', { scope: '/' })
        .catch(() => { /* SW unavailable — degrade silently */ });
    }

    const params = new URLSearchParams({ brand_id: brand._id });
    const base = (backendURL || '').replace(/\/$/, '');
    const manifestHref = `${base}/public/site/manifest.webmanifest?${params.toString()}`;
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = manifestHref;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);

    const themeMeta = document.createElement('meta');
    themeMeta.name = 'theme-color';
    themeMeta.content = brand.brand_color || '#422AFB';
    document.head.appendChild(themeMeta);

    let faviconLink = null;
    let prevFaviconHref = null;
    if (brand.logo_upload_path) {
      faviconLink = document.querySelector('link[rel="icon"]')
        || (() => {
          const l = document.createElement('link');
          l.rel = 'icon';
          document.head.appendChild(l);
          return l;
        })();
      prevFaviconHref = faviconLink.getAttribute('href');
      faviconLink.setAttribute('href', brand.logo_upload_path);
    }

    const prevTitle = document.title;
    if (brand.name) document.title = brand.name;

    return () => {
      if (link.parentNode) link.parentNode.removeChild(link);
      if (themeMeta.parentNode) themeMeta.parentNode.removeChild(themeMeta);
      if (faviconLink && prevFaviconHref != null) {
        faviconLink.setAttribute('href', prevFaviconHref);
      } else if (faviconLink && !prevFaviconHref) {
        faviconLink.parentNode && faviconLink.parentNode.removeChild(faviconLink);
      }
      document.title = prevTitle;
    };
  }, [brand?._id, brand?.name, brand?.brand_color, brand?.logo_upload_path]);
}
